<template>
  <v-app>
    <AppWrapperView>
      <v-main>
        <v-container class="pa-8" fluid>
          <v-alert
            v-if="errorMessage"
            type="error"
            density="compact"
            :text="errorMessage"
            closable
            class="mb-4"
            @click:close="errorMessage = ''"
          />

          <!-- 絞り込み -->
          <v-row>
            <v-col cols="6">
              <!-- 店舗が多い（約700）ので、店舗コード・店舗名を打って候補を絞り込めるようにする（2026-10-23）。
                   店舗は複数選べる。先頭の「全店舗」（または未選択）なら、選べる店舗すべてが対象（2026-10-24）。
                   auto-select-first は付けない。付けると打ち込んだときに先頭の店舗が強調され、選ばれたように見える（2026-11-01 打合せ・S-41。棚卸画面に合わせる） -->
              <v-autocomplete
                v-model="searchCondition.shopCodes"
                @update:model-value="shopSearchGuard.selected"
                :search="shopSearch"
                @update:search="onShopSearch"
                :items="shopOptionsShown"
                item-title="code_name"
                item-value="code"
                density="compact"
                variant="outlined"
                hide-details="auto"
                multiple
                chips
                closable-chips
                clearable
                :label="$t('tanaoroshiInventoryDate.label.shop')"
              />
            </v-col>
            <v-col cols="3">
              <YearMonthPicker
                ref="yearMonthPicker"
                variant="outlined"
                v-model="searchCondition.yearMonth"
                :label="$t('tanaoroshiInventoryDate.label.yearMonth')"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="text-right">
              <v-btn class="px-8" elevation="2" @click="clear">{{ $t('common.action.clear') }}</v-btn>
              <v-btn
                theme="dark"
                class="ml-4 px-8 bg-grey-darken-3"
                elevation="2"
                :loading="isSearching"
                @click="search()"
              >{{ $t('common.action.search') }}</v-btn>
            </v-col>
          </v-row>

          <!-- 登録 -->
          <v-row>
            <v-col cols="12" class="text-right">
              <v-btn
                dark
                class="px-6 bg-indigo-darken-1"
                elevation="2"
                @click="openInventoryDateDialog"
              >{{ $t('tanaoroshi.action.inventoryDate') }}</v-btn>
              <v-btn
                dark
                class="ml-4 px-6 bg-indigo-darken-1"
                elevation="2"
                @click="inventoryDateBulkDialogVisible = true"
              >{{ $t('tanaoroshi.action.inventoryDateBulk') }}</v-btn>
            </v-col>
          </v-row>

          <!-- 一覧 -->
          <v-table density="comfortable" class="mt-4 list_table">
            <thead>
              <tr>
                <th>{{ $t('tanaoroshiInventoryDate.label.table.siteCode') }}</th>
                <th>{{ $t('tanaoroshiInventoryDate.label.table.siteName') }}</th>
                <th>{{ $t('tanaoroshiInventoryDate.label.table.inventoryDate') }}</th>
                <th>{{ $t('tanaoroshiInventoryDate.label.table.status') }}</th>
                <th class="action_col">{{ $t('tanaoroshiInventoryDate.label.table.action') }}</th>
              </tr>
            </thead>
            <tbody>
              <!-- 新しい棚卸日が上。月が変わるところに見出し行を入れる -->
              <!-- 1ページの件数で区切る（他の画面と同じページャ。CR-002 S-25）。月の見出しの件数はその月の全件 -->
              <template v-for="group in monthGroups" :key="group.yearMonth">
              <tr class="month_row">
                <td colspan="5">{{ $t('tanaoroshiInventoryDate.label.monthHeader', { year: group.year, month: group.month, count: monthCount[group.yearMonth] ?? group.rows.length }) }}</td>
              </tr>
              <tr v-for="row in group.rows" :key="row.tanaoroshiId ?? (row.siteCode + row.inventoryDate)">
                <td>{{ row.siteCode }}</td>
                <td>{{ row.siteName }}</td>
                <td>{{ row.inventoryDate }}</td>
                <td>{{ row.statusLabel }}</td>
                <td class="action_col">
                  <!-- 確定した棚卸は変更できない -->
                  <v-btn
                    size="small"
                    variant="outlined"
                    color="indigo-darken-1"
                    :disabled="row.statusId === 5 || row.tanaoroshiId == null"
                    @click="openEdit(row)"
                  >{{ $t('tanaoroshiInventoryDate.action.edit') }}</v-btn>
                </td>
              </tr>
              </template>
              <tr v-if="searched && rows.length === 0">
                <td colspan="5" class="text-grey">{{ $t('common.message.noData') }}</td>
              </tr>
            </tbody>
          </v-table>

          <v-row>
            <v-col cols="3"></v-col>
            <v-col cols="6">
              <v-pagination v-model="page" :length="pageCount" :total-visible="9" />
            </v-col>
            <v-col cols="3">
              <v-row>
                <v-spacer></v-spacer>
                <v-col cols="10" class="d-flex justify-end">
                  <v-select density="compact" variant="outlined" hide-details="auto" v-model="displayPerPage"
                    :items="displayPerPageItem" item-title="name" item-value="value"
                    :label="$t('common.label.displayCountPerPage')" return-object
                    @update:modelValue="page = 1" />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" class="text-right">
                  {{ displayRange.minNo }} - {{ displayRange.maxNo }}{{ $t('common.label.displayedDataRange') }} / {{ rows.length }}{{ $t('common.label.totalCountOf') }}
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-container>

        <!-- 棚卸日の編集（日付だけ。確定するまで） -->
        <v-dialog v-model="editVisible" width="460" :persistent="isEditing">
          <v-card>
            <v-card-title><h3><strong>{{ $t('tanaoroshiInventoryDate.label.editTitle') }}</strong></h3></v-card-title>
            <v-card-text>
              <div class="mb-3">
                {{ $t('tanaoroshiInventoryDate.label.editTarget', { shop: editRow.siteCode + '_' + (editRow.siteName ?? ''), id: editRow.tanaoroshiId, status: editRow.statusLabel }) }}
              </div>
              <!-- 日付はお客様の環境（ブラウザの言語）によらず日本の形（yyyy-MM-dd・日曜始まりのカレンダー）で出す。ディセントの画面と同じ DatePicker（2026-11-02 依頼）。ダイアログの中なのでカレンダーは body に出す -->
              <DatePicker
                v-model="editDate"
                variant="outlined"
                teleport="body"
                :label="$t('tanaoroshiInventoryDate.label.table.inventoryDate')"
              />
              <div class="text-caption mt-2 text-grey-darken-1">{{ $t('tanaoroshiInventoryDate.message.editNote') }}</div>
              <div v-if="editError" class="mt-2" style="color: #c62828">{{ editError }}</div>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions class="pa-4">
              <v-spacer></v-spacer>
              <v-btn text :disabled="isEditing" @click="editVisible = false">{{ $t('common.action.cancel') }}</v-btn>
              <v-btn color="indigo-darken-1" dark :loading="isEditing"
                     :disabled="!editDate || editDate === editRow.inventoryDate" @click="saveEdit">
                {{ $t('tanaoroshiInventoryDate.action.save') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <TanaoroshiInventoryDateDialog
          v-if="inventoryDateDialogVisible"
          :dialog-data="inventoryDateDialog"
          :handle-cancel="closeInventoryDateDialog"
        />
        <TanaoroshiInventoryDateBulkDialog
          v-if="inventoryDateBulkDialogVisible"
          :handle-cancel="closeInventoryDateBulkDialog"
        />
      </v-main>
    </AppWrapperView>
  </v-app>
</template>

<script>
import AppWrapperView from "@/components/views/AppWrapperView";
import YearMonthPicker from "@/components/YearMonthPicker";
import DatePicker from "@/components/DatePicker";
import TanaoroshiInventoryDateDialog from "@/components/dialog/TanaoroshiInventoryDateDialog";
import TanaoroshiInventoryDateBulkDialog from "@/components/dialog/TanaoroshiInventoryDateBulkDialog";
import api from "@/apis/staff";
import constant from "@/utils/constant";
import utils from "@/utils";
import { ALL_SHOPS, withAllShops, normalizeShopSelection, selectedShopCodes, limitShopOptions, createShopSearchGuard } from "@/utils/shopSelection";

/**
 * 棚卸日登録
 *
 * 店舗ごとの棚卸日を登録する（1件ずつ／Excel で一括）。
 * 登録すると棚卸IDが発行され、棚卸画面に「未実施」で並ぶ。
 * 下の一覧は登録済みの棚卸日で、店舗（複数選べる）と棚卸年月で絞り込める。
 * 「全店舗」を選んだとき・店舗を選ばずに検索したときは、プルダウンに出ている店舗（自店舗）すべてが対象。
 */
export default {
  name: "TanaoroshiInventoryDatePage",
  components: { AppWrapperView, YearMonthPicker, DatePicker, TanaoroshiInventoryDateDialog, TanaoroshiInventoryDateBulkDialog },

  data() {
    return {
      shopItem: [],
      // 店舗は複数選べる。初期値は「全店舗」（2026-10-24）
      searchCondition: { shopCodes: [ALL_SHOPS], yearMonth: "" },
      // 最後に「検索」した条件。登録・変更・一括登録のあとの読み直しはこの条件で行う
      // （入力欄に打っただけで「検索」を押していない条件で検索されてしまっていた。2026-11-04 受入テスト）
      appliedCondition: null,
      // 店舗の欄に打っている文字（候補の絞り込み用）
      shopSearch: "",
      // 店舗を選んだ直後に打った文字が消えないようにする（createShopSearchGuard）
      shopSearchGuard: createShopSearchGuard(),
      rows: [],
      // 1ページの表示件数（他の画面と同じ選択肢・既定50件。CR-002 S-25）
      displayPerPage: utils.getDisplayPerPage(this.$t),
      displayPerPageItem: utils.getDisplayPerPageItem(this.$t),
      page: 1,
      searched: false,
      isSearching: false,
      errorMessage: "",
      inventoryDateDialogVisible: false,
      inventoryDateBulkDialogVisible: false,
      inventoryDateDialog: { shopItem: [], shopCode: null },
      // 棚卸日の編集
      editVisible: false,
      isEditing: false,
      editRow: {},
      editDate: "",
      editError: "",
    };
  },

  mounted() {
    api.post("/store/search", { selectSiteFlag: 1 })
      .then((response) => {
        this.shopItem = (response.data.results?.shopList ?? []).map((e) => ({
          name: e.name, code: e.code, code_name: e.code + "_" + e.name,
        }));
        // 開いたときに、自店舗すべての棚卸日を出しておく
        this.search();
      })
      .catch(() => {
        //do nothing
      });
  },

  watch: {
    // 件数が減って最後のページが無くなったら、残っている最後のページへ
    pageCount(n) {
      if (this.page > n) this.page = n;
    },
    // 「全店舗」と個別の店舗を同時に選ばないよう直す（2026-10-24）
    "searchCondition.shopCodes"(value, oldValue) {
      const fixed = normalizeShopSelection(value, oldValue);
      if (fixed !== value) this.searchCondition.shopCodes = fixed;
    },
  },

  computed: {
    // 店舗の候補（先頭に「全店舗」。2026-10-24）
    shopOptions() {
      return withAllShops(this.shopItem, this.$t('common.label.allShops'));
    },
    // 欄に渡す候補（打った文字に当たる先頭100件＋全店舗＋選択済み。2026-10-26 S-32）
    shopOptionsShown() {
      return limitShopOptions(this.shopOptions, this.shopSearch, this.searchCondition.shopCodes);
    },
    // 1ページの件数。「すべて」（0）は全件
    pageSize() {
      const v = this.displayPerPage?.value ?? 50;
      return v > 0 ? v : Math.max(1, this.rows.length);
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.rows.length / this.pageSize));
    },
    pagedRows() {
      const start = (this.page - 1) * this.pageSize;
      return this.rows.slice(start, start + this.pageSize);
    },
    // 「x - y件目」の表示
    displayRange() {
      const total = this.rows.length;
      if (total === 0) return { minNo: 0, maxNo: 0 };
      const minNo = (this.page - 1) * this.pageSize + 1;
      return { minNo, maxNo: Math.min(total, this.page * this.pageSize) };
    },
    // 年月ごとの全件数（月の見出しに出す。ページで切れても、その月の件数が分かるように）
    monthCount() {
      const count = {};
      for (const row of this.rows) {
        const yearMonth = (row.inventoryDate ?? "").slice(0, 7);
        count[yearMonth] = (count[yearMonth] ?? 0) + 1;
      }
      return count;
    },
    // 表示中のページの棚卸日（yyyy-MM-dd）を年月で区切る。並びは API の順（新しい棚卸日が上）のまま
    monthGroups() {
      const groups = [];
      for (const row of this.pagedRows) {
        const yearMonth = (row.inventoryDate ?? "").slice(0, 7);
        let g = groups[groups.length - 1];
        if (!g || g.yearMonth !== yearMonth) {
          g = { yearMonth, year: yearMonth.slice(0, 4), month: Number(yearMonth.slice(5, 7)), rows: [] };
          groups.push(g);
        }
        g.rows.push(row);
      }
      return groups;
    },
  },
  methods: {
    // 店舗欄の打った文字。店舗を選んだ直後に Vuetify が消すのは受け付けない（createShopSearchGuard）
    onShopSearch(value) {
      if (this.shopSearchGuard.accepts(value)) this.shopSearch = value;
    },
    // 「検索」ボタン・画面を開いたとき：いまの入力欄の条件で検索し、その条件を覚える
    search() {
      const c = this.searchCondition;
      this.appliedCondition = { ...c, shopCodes: [...(c.shopCodes ?? [])] };
      this.runSearch(this.appliedCondition);
    },
    // 登録・変更・一括登録のあとの読み直し：最後に「検索」した条件で検索する（入力欄の打ちかけは使わない）
    reload() {
      if (!this.appliedCondition) return this.search();
      this.runSearch(this.appliedCondition);
    },
    runSearch(condition) {
      // 「全店舗」または未選択なら、プルダウンの店舗すべて
      const selected = selectedShopCodes(condition.shopCodes, this.shopItem);
      const shopCodeList = selected.length > 0 ? selected : this.shopItem.map((e) => e.code);
      if (shopCodeList.length === 0) return;

      this.isSearching = true;
      this.errorMessage = "";
      api.post("/tanaoroshi/inventory-date/search", {
        shopCodeList: shopCodeList,
        yearMonth: condition.yearMonth || null,
      })
        .then((response) => {
          if (response.data.status != constant.RESPONSE_STATUS.SUCCESS) {
            this.errorMessage = response.data.errorMessage;
            this.rows = [];
            return;
          }
          this.rows = response.data.results?.inventoryDateList ?? [];
          this.page = 1;
        })
        .catch((error) => {
          this.errorMessage = utils.getErrorMsg(error.response, this.$t);
          this.rows = [];
        })
        .finally(() => {
          this.searched = true;
          this.isSearching = false;
        });
    },

    clear() {
      this.searchCondition.shopCodes = [ALL_SHOPS];
      this.searchCondition.yearMonth = "";
      this.$refs.yearMonthPicker?.clear();
    },

    // 1件登録。絞り込みで選んでいる店舗（複数なら1つ目。全店舗・未選択なら先頭の店舗）を初期値にする
    openInventoryDateDialog() {
      const first = (this.searchCondition.shopCodes ?? []).find((c) => c !== ALL_SHOPS);
      this.inventoryDateDialog = {
        shopItem: this.shopItem,
        shopCode: first ?? (this.shopItem[0]?.code ?? null),
      };
      this.inventoryDateDialogVisible = true;
    },

    openEdit(row) {
      this.editRow = row;
      this.editDate = row.inventoryDate;
      this.editError = "";
      this.editVisible = true;
    },
    saveEdit() {
      this.isEditing = true;
      this.editError = "";
      api.post("/tanaoroshi/inventory-date/update", {
        tanaoroshiId: this.editRow.tanaoroshiId,
        inventoryDate: this.editDate,
      })
        .then((response) => {
          if (response.data.status != constant.RESPONSE_STATUS.SUCCESS) {
            this.editError = response.data.errorMessage;
            return;
          }
          this.editVisible = false;
          this.reload();
        })
        .catch((error) => {
          this.editError = utils.getErrorMsg(error.response, this.$t);
        })
        .finally(() => {
          this.isEditing = false;
        });
    },

    // 登録した分を一覧に出すため、閉じたら検索し直す
    closeInventoryDateDialog() {
      this.inventoryDateDialogVisible = false;
      this.reload();
    },
    closeInventoryDateBulkDialog() {
      this.inventoryDateBulkDialogVisible = false;
      this.reload();
    },
  },
};
</script>

<style scoped>
.list_table :deep(th) {
  font-size: 14px !important;
  font-weight: 700 !important;
  color: #5b6472 !important;
}
.list_table :deep(td) {
  font-size: 14px !important;
}
.month_row td {
  background-color: #e8eaf6;
  color: #283593;
  font-weight: bold;
  height: 34px !important;
}
.action_col {
  width: 110px;
  text-align: center !important;
}
</style>
